import {useState} from 'react';
import {useIntl} from 'react-intl';
import {Button as BootstrapButton, Card, Col, Form, Row} from 'react-bootstrap';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import {useGetConferenceRooms, useModifierSalleConference} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import Loading from '../../../../../app/common/components/Loading';

/**
 * MAQUETTE — reproduction de « ConferenceRoom.aspx » : liste des salles de conférence,
 * chacune avec 3 modes exclusifs (affichage / renommer / envoyer les identifiants par
 * email). Pas de création/suppression de salle en self-service, comme dans le legacy.
 */

const EMAIL_REGEX = /^[A-Z0-9._%-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

type Mode = 'affichage' | 'edition' | 'email';

const ConferenceRoom = (): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading} = useGetConferenceRooms();
  const {mutate: modifierSalle} = useModifierSalleConference();
  const [salleActiveId, setSalleActiveId] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>('affichage');
  const [nomEdite, setNomEdite] = useState('');
  const [emailsEdites, setEmailsEdites] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useAutoDismissMessage();

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  const ouvrirEdition = (id: number, nomActuel: string): void => {
    setSalleActiveId(id);
    setMode('edition');
    setNomEdite(nomActuel);
    setErreur(null);
  };

  const ouvrirEmail = (id: number): void => {
    setSalleActiveId(id);
    setMode('email');
    setEmailsEdites('');
    setErreur(null);
  };

  const annuler = (): void => {
    setSalleActiveId(null);
    setMode('affichage');
    setErreur(null);
  };

  const enregistrerNom = (): void => {
    if (nomEdite.trim() === '') {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.ERREUR_NOM'}));
      return;
    }
    if (salleActiveId != null) {
      modifierSalle({id: salleActiveId, nom: nomEdite.trim()});
    }
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
    setSalleActiveId(null);
    setMode('affichage');
  };

  const envoyerEmails = (): void => {
    const adresses = emailsEdites
      .split(';')
      .map((adresse) => adresse.trim())
      .filter((adresse) => adresse !== '');
    if (adresses.length === 0 || !adresses.every((adresse) => EMAIL_REGEX.test(adresse))) {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.ERREUR_EMAIL'}));
      return;
    }
    setMessage(
      intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.EMAIL_ENVOYE'}, {emails: adresses.join(', ')})
    );
    setSalleActiveId(null);
    setMode('affichage');
  };

  return (
    <div>
      <Card bg="light" className="mb-5">
        <Card.Body>
          <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.TITLE'})} />
          <p className="text-primary fs-6 mb-0">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.DESCRIPTION'})}
          </p>
        </Card.Body>
      </Card>

      {message && <div className="fw-bold text-danger mb-3">{message}</div>}

      {data.salles.length === 0 && (
        <p className="text-muted fs-6">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.AUCUNE_SALLE'})}
        </p>
      )}

      <Row className="g-5">
        {data.salles.map((salle) => (
          <Col key={salle.id} xs={12} xl={6}>
            <Card className="h-100">
              <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                {salleActiveId === salle.id && mode === 'edition' ? (
                  <div className="w-100">
                    <Form.Control
                      value={nomEdite}
                      onChange={(e) => setNomEdite(e.target.value)}
                      style={{maxWidth: 260}}
                    />
                    {erreur && <div className="text-danger fs-7 mt-1">{erreur}</div>}
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-3">
                      <i className="bi bi-camera-video-fill text-primary fs-4" />
                      <Card.Title className="m-0">
                        <Level3Title title={salle.nom} />
                      </Card.Title>
                    </div>
                    <div className="d-flex gap-3">
                      <i
                        className="bi bi-pencil text-primary cursor-pointer fs-5"
                        title={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
                        onClick={() => ouvrirEdition(salle.id, salle.nom)}
                      />
                      <i
                        className="bi bi-envelope-fill text-primary cursor-pointer fs-5"
                        title={intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.ENVOYER'})}
                        onClick={() => ouvrirEmail(salle.id)}
                      />
                    </div>
                  </>
                )}
              </Card.Header>
              <Card.Body>
                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.NUMERO'})}</th>
                      <td>{salle.numero}</td>
                    </tr>
                    <tr>
                      <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.CODE_ADMIN'})}</th>
                      <td>{salle.codeAdmin}</td>
                    </tr>
                    <tr>
                      <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.CODE_UTILISATEUR'})}</th>
                      <td>{salle.codeUtilisateur}</td>
                    </tr>
                  </tbody>
                </table>

                {salleActiveId === salle.id && mode === 'email' && (
                  <div className="mt-4">
                    <div className="fs-7 mb-2">
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.EMAIL_INSTRUCTION'})}
                    </div>
                    <Form.Control
                      value={emailsEdites}
                      onChange={(e) => setEmailsEdites(e.target.value)}
                      placeholder="email1@exemple.com; email2@exemple.com"
                    />
                    {erreur && <div className="text-danger fs-7 mt-1">{erreur}</div>}
                  </div>
                )}

                {salleActiveId === salle.id && mode !== 'affichage' && (
                  <div className="d-flex justify-content-end gap-3 mt-4">
                    <BootstrapButton variant="secondary" size="sm" onClick={annuler}>
                      {intl.formatMessage({id: 'CANCEL'})}
                    </BootstrapButton>
                    <BootstrapButton
                      variant="primary"
                      size="sm"
                      onClick={mode === 'edition' ? enregistrerNom : envoyerEmails}
                    >
                      {intl.formatMessage({
                        id:
                          mode === 'edition'
                            ? 'TELEPHONIE.SVI.MENU.FORM.SAVE'
                            : 'TELEPHONIE.PBX.CONFERENCE.ENVOYER',
                      })}
                    </BootstrapButton>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="text-muted fs-7 mt-5">
        {intl.formatMessage({id: 'TELEPHONIE.PBX.CONFERENCE.COMMENT_AJOUTER'})}
      </div>
    </div>
  );
};

export default ConferenceRoom;
